import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Divider,
  useTheme,
  Paper
} from '@mui/material';
import DeveloperModeIcon from '@mui/icons-material/DeveloperMode';
import { auth } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../../contexts/SessionContext';

interface DevLoginBypassProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
}

const DevLoginBypass: React.FC<DevLoginBypassProps> = ({ onSuccess, onError }) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const { login } = useSession();

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleDevLogin = async () => {
    setError(null);
    setIsLoading(true);
    
    try {
      console.log('Attempting development login bypass...');
      const { token } = await auth.devLogin();
      console.log('Development login successful, received token');
      
      // Use SessionContext login method to immediately update authentication state
      login(token);
      
      if (onSuccess) {
        onSuccess();
      } else {
        navigate('/dashboard');
      }
    } catch (err: any) {
      console.error('Development login error:', err);
      const errorMessage = err.response?.data?.msg || err.message || 'Development login failed';
      setError(errorMessage);
      if (onError) {
        onError(errorMessage);
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Paper
      elevation={3}
      sx={{
        padding: 3,
        marginTop: 2,
        background: 'linear-gradient(135deg, #FF6B35 0%, #F7931E 100%)',
        border: '2px dashed #FF4500',
        borderRadius: 2,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'repeating-linear-gradient(45deg, transparent, transparent 10px, rgba(255, 255, 255, 0.1) 10px, rgba(255, 255, 255, 0.1) 20px)',
          pointerEvents: 'none',
        }
      }}
    >
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            marginBottom: 2,
            color: 'white'
          }}
        >
          <DeveloperModeIcon sx={{ fontSize: 24 }} />
          <Typography variant="h6" sx={{ fontWeight: 700, color: 'white' }}>
            🚧 DEVELOPMENT MODE
          </Typography>
        </Box>

        <Typography 
          variant="body2" 
          sx={{ 
            marginBottom: 2, 
            color: 'white',
            fontWeight: 500,
            lineHeight: 1.5
          }}
        >
          Skip the login process and authenticate with a default development account.
          This feature is only available in development mode.
        </Typography>

        <Box sx={{ marginBottom: 2 }}>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', fontWeight: 600 }}>
            Default Dev Account:
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block' }}>
            📧 dev@altifi.com
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)', display: 'block' }}>
            🔑 DevPassword123!
          </Typography>
        </Box>

        {error && (
          <Alert 
            severity="error" 
            sx={{ 
              marginBottom: 2,
              backgroundColor: 'rgba(255, 255, 255, 0.9)',
              '& .MuiAlert-message': {
                color: theme.palette.error.dark
              }
            }}
          >
            {error}
          </Alert>
        )}

        <Button
          variant="contained"
          onClick={handleDevLogin}
          disabled={isLoading}
          fullWidth
          sx={{
            backgroundColor: 'rgba(255, 255, 255, 0.2)',
            color: 'white',
            fontWeight: 700,
            fontSize: '0.875rem',
            textTransform: 'none',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              transform: 'translateY(-1px)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            },
            '&:disabled': {
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              color: 'rgba(255, 255, 255, 0.7)',
            }
          }}
        >
          {isLoading ? 'Logging in...' : '🚀 Dev Login Bypass'}
        </Button>

        <Divider 
          sx={{ 
            marginTop: 2, 
            marginBottom: 1,
            borderColor: 'rgba(255, 255, 255, 0.3)'
          }} 
        />
        
        <Typography 
          variant="caption" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.8)',
            fontStyle: 'italic',
            display: 'block',
            textAlign: 'center'
          }}
        >
          ⚠️ This component is automatically hidden in production builds
        </Typography>
      </Box>
    </Paper>
  );
};

export default DevLoginBypass; 